import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest, GraphqlError } from 'core/utils/backend-service';
import { DeviceTypeDetails } from 'modules/devices/domain/api/device-type.api';
import { getDeviceConfigurationList } from 'modules/settings/pods/settings-devices/settings-devices-configuration/service/settings-devices-configuration.service';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { ConfigurationListContext, RefreshInterfaceConfigProps } from '../configuration-list.context';

interface ConfigurationListProviderProp extends PropsWithChildren {
    deviceType?: DeviceTypeDetails;
    enabled?: boolean;
}

export const ConfigurationListProvider: React.FC<ConfigurationListProviderProp> = ({
    children,
    deviceType,
    enabled = true,
}) => {
    const deviceTypeSelected = useMemo(() => deviceType, [deviceType?.id]);
    const [selectedTab, setSelectedTab] = useState<string>('tab1');
    const [showOnlyActiveConfigs, setShowOnlyActiveConfigs] = useState<boolean>(true);
    const [selectedRows, setSelectedRows] = useState<DeviceConfigurationInfo[]>([]);

    // Reset selection on tab switch (enabled change). The device-type reset is
    // owned by useConfigurationList, so we only react to `enabled` here.
    useEffect(() => {
        setSelectedRows([]);
    }, [enabled]);

    const [configurationSelected, setConfigurationSelected] = useState<DeviceConfigurationInfo | undefined>(undefined);

    const configListKey = settingsKeys.configList({
        deviceTypeId: deviceTypeSelected?.id,
        showOnlyActiveConfigs,
    });

    const {
        data: configurations = [],
        isFetching: isLoading,
        error: queryError,
    } = usePocQuery<DeviceConfigurationInfo[]>({
        // Pure fetcher — returns data or rejects; error/selection handling lives in the effect below.
        queryKey: configListKey,
        queryFn: () => executeRequest(() => getDeviceConfigurationList(deviceTypeSelected!.id, showOnlyActiveConfigs)),
        enabled: !!deviceTypeSelected,
    });

    // The list endpoint returns its GraphqlError as a JSON-encoded message; surface the first error and
    // clear any stale selection when the fetch fails (was previously done as a side effect in queryFn).
    const error = useMemo<GraphqlError | undefined>(() => {
        if (!queryError) {
            return undefined;
        }
        try {
            return JSON.parse(queryError.message).errors[0];
        } catch {
            return undefined;
        }
    }, [queryError]);

    useEffect(() => {
        if (queryError) {
            setConfigurationSelected(undefined);
        }
    }, [queryError]);

    // Optionally waits a caller-provided delay, clears the current selection, then invalidates the list query.
    const refreshConfig = async (config?: RefreshInterfaceConfigProps): Promise<void> => {
        if (config?.fetchingDelay) {
            await new Promise((resolve) => setTimeout(resolve, config.fetchingDelay));
        }
        setConfigurationSelected(undefined);
        await updateQuery(settingsKeys.configurations());
    };

    const value = useMemo(
        () => ({
            deviceTypeSelected,
            selectedTab,
            isLoading,
            configurations,
            configurationSelected,
            error,
            selectedRows,
            showOnlyActiveConfigs,
            refreshConfigurationList: refreshConfig,
            setSelectedTab,
            setConfigurationSelected,
            setSelectedRows,
            setShowOnlyActiveConfig: setShowOnlyActiveConfigs,
        }),
        [selectedTab, isLoading, configurations, selectedRows, configurationSelected, error]
    );
    return <ConfigurationListContext.Provider value={value}>{children}</ConfigurationListContext.Provider>;
};
